/* eslint-disable react/no-unknown-property */
import { Icon, PopoverTooltip, Tooltip } from '@pypestream/design-system';
import { Tenancy_Rich_Asset_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { Cell as TableCell } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useCallback,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';

import {
  tableStore,
  TableRow,
  emitOnce,
  _Node,
  NodeColumns,
  nodeTypeDependentCells,
} from '../../../stores/table-store';
import { useProStudioCtxSelector } from '../../../xstate/app.xstate';
import { readAgentCompilerMode } from '../../../utils/agent-compiler-mode';
import { getCommonPinningStyles } from '../utils/get-common-pinning-styles';
import { getAssetTypeList, ASSET_LABELS } from '../utils/get-asset-type-list';
import { NodeBehaviorsMultiSelect } from '../node-behaviors-multi-select';
import { shouldOpenPropertyEditorOnClick } from '../expanded-cell-view/should-open-on-click';
import { RowContextMenu } from './row-context-menu';
import { resolveDelayedContextMenuClose } from './row-context-menu-close';
import { resolveMenuCloseSignal } from './menu-close-signal';
import { CellValueContextMenu } from './cell-value-context-menu';
import { resolveNodeRightClickMenuKind } from './cell-context-menu-kind';
import {
  cellErrorDescriptionId,
  joinCellErrorMessages,
  resolveCellErrorAria,
} from './cell-error-message';
import {
  buildScriptFileNames,
  cellErrorKey,
  CHECKBOX_COLUMNS,
  READ_ONLY_COLUMNS,
} from '../../../stores/utils';
import { isWrappableTextColumn } from '../../../stores/utils/column-edit-kind';
import { isColumnWrapEnabled } from '../../../stores/utils/column-wrap-settings';
import { isJsonCapableColumn } from '../../../stores/utils/is-json-capable-column';
import {
  TEXT_WRAP_VIEW_STYLE,
  classifyEditorWrapClose,
  columnStoresNewlines,
  draftWithoutStoredNewlines,
  resolveEditorCellMaxHeight,
  resolveTextCellPresentation,
  shouldCollapseEditorWrap,
} from './editor-cell-layout';
import { computeRevealScrollDelta } from '../../../stores/utils/reveal-scroll';
import { selectStoredTextForTypeToEdit } from '../../../stores/utils/type-to-edit-focus';
import { releasePanelSelection } from '../expanded-cell-view/release-panel-selection';
import { AnalyticsEventSelect } from '../expanded-cell-view/analytics-event-select';
import { analyticsEventCellLabel } from '../expanded-cell-view/analytics-events';
import { commitAnalyticsEventChoice } from '../expanded-cell-view/commit-analytics-event-choice';
import {
  ARRAY_INPUT_SEPARATORS,
  arrayCellText,
  stringifyValue,
} from '../../../stores/native-cell-value';
import './cell.scss';

// PSTUDIO-2406 perf experiment (branch: experiment/pstudio-2406-native-cells)
// Render every cell with native HTML controls: <input>, <textarea>,
// <select>, <input type="checkbox"> — bypassing every column-specific React
// cell component (NodeNameCell, NodeTypeCellComponent, UtterancesCell,
// MessageCell, RichAssetTypeCell, …) and every design-system web component
// they would otherwise mount inside (Select, Checkbox, TextInput, MultiSelect, …).
//
// Goals:
// - measure how much render cost the column-specific cells + design-system
//   web components add vs. just the table chrome
// - keep the app *functional*: checkboxes click, selects show labels, text
//   cells edit on blur, row resize handle still works (it lives on the
//   DraggableRow's index cell, not here)

const TEXTAREA_COLUMNS = new Set<string>([
  'message',
  'utterances',
  'parameterInput',
  'richAssetContent',
  'analyticsEvent',
]);

const cellInputStyle: React.CSSProperties = {
  // Native <input>/<select> carry an intrinsic min-width (~size attr) that
  // stops them shrinking inside the flex cell wrapper, which shifted/clipped
  // the text when a freshly-mounted input replaced the view <span>. minWidth:0
  // lets the control shrink to the cell width exactly like the span did.
  minWidth: 0,
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  font: 'inherit',
  color: 'inherit',
  padding: '4px 8px',
  boxSizing: 'border-box',
};

const cellTextareaStyle: React.CSSProperties = {
  ...cellInputStyle,
  resize: 'none',
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
};

// PSTUDIO-4026: after focusing the edit control with { preventScroll: true } (so
// the browser never auto-scrolls the table), keyboard-driven entry to an
// off-screen cell must still bring it into view — but ONLY when it is genuinely
// outside the scroller's visible band, so an already-visible cell never moves.
//
// The band's top is offset PAST the sticky <thead> (which overlays the top of the
// scroller); a bare scrollIntoView({block:'nearest'}) would tuck the cell UNDER
// the header. Reuse the same geometry the reveal (PSTUDIO-3848) already uses —
// computeRevealScrollDelta with viewTop = scroller.top + headerHeight — so the
// cell lands just below the header, never behind it.
function scrollCellIntoViewIfNeeded(
  td: HTMLElement | null,
  scroller: HTMLElement | null | undefined
) {
  if (!td || !scroller) return;
  const cellRect = td.getBoundingClientRect();
  const scRect = scroller.getBoundingClientRect();
  const header = scroller.querySelector('thead');
  const headerHeight = header ? header.getBoundingClientRect().height : 0;
  const delta = computeRevealScrollDelta({
    viewTop: scRect.top + headerHeight,
    // clientHeight excludes a horizontal scrollbar (unlike rect.bottom).
    viewBottom: scRect.top + scroller.clientHeight,
    firstTop: cellRect.top,
    lastBottom: cellRect.bottom,
  });
  if (delta !== 0) scroller.scrollTop += delta;
  // PSTUDIO-4026: { preventScroll: true } also suppresses the native HORIZONTAL
  // reveal the old bare focus() relied on. Restore it with the same 1-D geometry
  // along x — no sticky-header offset on this axis; clientWidth excludes any
  // vertical scrollbar (unlike rect.right).
  const dx = computeRevealScrollDelta({
    viewTop: scRect.left,
    viewBottom: scRect.left + scroller.clientWidth,
    firstTop: cellRect.left,
    lastBottom: cellRect.right,
  });
  if (dx !== 0) scroller.scrollLeft += dx;
}

// View-mode display for an inactive cell: a single lightweight node that stands
// in for the live control. The `c-editor-table__cell-view` class supplies the
// horizontal overlay (negative margin-inline + width) so the span's text sits at
// the same x as the control's; this inline style matches the control's
// horizontal padding (`cellInputStyle` uses `4px 8px`) and clamps long values to
// one line. It deliberately sets NO height/vertical padding — the flex-centered
// `.c-editor-cell__inner` centers the single line, matching the control's
// natively-centered text, so nothing hops vertically on focus (PSTUDIO-3153).
const cellViewStyle: React.CSSProperties = {
  paddingInline: '8px',
  font: 'inherit',
  color: 'inherit',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

// PSTUDIO-3990 (a11y): an errored cell is painted red
// (`c-editor-table-td--error`) and its message is shown in a
// `<ps-popover-tooltip>` that `CellErrorTooltip` PORTALS to `document.body` —
// i.e. outside the `<td>`, and inside the DS component's `visibility: hidden`
// shadow container until it is shown. Neither is programmatically associated
// with the cell (WCAG 1.3.1 / 1.4.1 / 3.3.1), so the same text is ALSO rendered
// onto a plain node that `aria-describedby` can reach.
//
// That node lives INSIDE THE PORTAL, next to the tooltip — never inside the
// `<td>`. `display: none` keeps it out of `innerText` and out of
// name-from-contents, but NOT out of `textContent`: `Node.textContent` is
// defined over the DOM tree and ignores CSS entirely, so a hidden node inside
// the cell still joins it. `textContent` is exactly what the Playwright suite
// reads — `toHaveText` compares it, and `EditorTableLocator.getNodeNumbers()`
// reads `td.textContent` — so an in-cell description made every text assertion
// on an invalid cell read `<value><message>` from the moment the cell became
// invalid, and this ticket is what makes a blank `Node #` invalid. Measured:
// `getNodeNumbers()` returned
// `'2Duplicate node number. Node numbers must be unique.'` for a cell showing
// `2`, which zeroed the duplicate count in the auto-renumber e2e and left
// `test-duplicate-node-number-survives-reload`'s teardown computing
// `Math.max(0, NaN) + 1` — so it never cleared the duplicate it had seeded and
// every later test in the serial suite inherited it.
//
// accname §4.1 still uses a hidden node when it is DIRECTLY referenced by
// `aria-describedby`, and an id reference resolves document-wide, so moving it
// out of the cell costs the description nothing.
const cellErrorDescriptionStyle: React.CSSProperties = {
  display: 'none',
};

function CellViewText({ text, wrap }: { text: string; wrap?: boolean }) {
  return (
    <span
      className="c-editor-table__cell-view"
      style={wrap ? TEXT_WRAP_VIEW_STYLE : cellViewStyle}
      title={text || undefined}
    >
      {text}
    </span>
  );
}

// View-mode display for an inactive SELECT cell (Node Type / Asset Type /
// Command). A lightweight <span> (no <select>, no option list) styled to look
// like the closed dropdown: shares the control's box (`c-editor-table__cell-view`)
// plus the chevron + reserved end-padding (`c-editor-table__cell-select`), so the
// text + width line up exactly between the closed and focused states.
function CellViewSelect({ label }: { label: string }) {
  return (
    <span
      className="c-editor-table__cell-view c-editor-table__cell-select"
      style={cellViewStyle}
      title={label || undefined}
    >
      {label}
    </span>
  );
}

// PSTUDIO-2425 hotfix: the PIF compiler requires nluDisabled=true whenever the
// file_upload asset type is specified, so the disable_input rule pins those
// rows to checked instead of clearing them.
function isFileUploadRow(rowId: string) {
  return (
    tableStore.getValue(rowId, NodeColumns.richAssetType) ===
    Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload
  );
}

// Behaviors commit. When "Disable Free Text" (`disable_input`) is included, NLU
// never runs, so NLU Disabled is not applicable: clear the stored `nluDisabled`
// flag in the SAME row update to keep the data honest and avoid an invalid
// compiler config (mirrors the original BehaviorsCell.onEdit; the perf refactor
// moved behaviors rendering off that column-def `cell` into NativeCellValue and
// dropped this clearing — PSTUDIO-2425). Exception: file_upload rows must keep
// nluDisabled=true (compiler requirement), so the rule pins them checked
// instead. The NativeCellValue checkbox path masks the box the same way while
// the rule applies, so this keeps the box consistent after the rule is later
// removed.
// Exported (PSTUDIO-3624) so the Expanded Cell View panel's behaviors editor
// commits through the exact same path as the table cell — including the
// nluDisabled side-effect when "Disable Free Text" is toggled.
export function writeBehaviors(rowId: string, values: TableRow['behaviors']) {
  const freeTextClosed =
    Array.isArray(values) && values.includes('disable_input');
  tableStore.recordRowEdit(
    rowId,
    `Edit ${NodeColumns.behaviors}`,
    () => {
      tableStore.updateRow({
        rowId,
        updates: {
          [NodeColumns.behaviors]: values,
          ...(freeTextClosed
            ? { [NodeColumns.nluDisabled]: isFileUploadRow(rowId) }
            : {}),
        } as Partial<TableRow>,
        skipValidationRules: false,
        saveImmediately: true,
      });
      tableStore.clearCellValidationError(rowId, NodeColumns.behaviors);
    },
    NodeColumns.behaviors
  );
}

// Exported (PSTUDIO-3624) so the Expanded Cell View panel's asset-type editor
// commits through the exact same path as the table cell — auto-adjusting
// behaviors/nluDisabled (PSTUDIO-3422) and seeding default content, all as one
// undo/redo command.
export function commitAssetTypeChange(rowId: string, newType: string) {
  // The logic lives in the store since PSTUDIO-4348, so a pasted type reaches
  // the very same path as this dropdown.
  tableStore.commitAssetTypeChange(rowId, newType);
}

// Exported (PSTUDIO-3624) so the panel's command editor commits identically to
// the grid — only a known function name is stored (otherwise cleared). A PASTE
// into a Command cell keeps the name as pasted (PROPOSED rail 19 of
// KEYBOARD_CLIPBOARD_BEHAVIOR.md, PSTUDIO-4348): it does not go through here.
export function commitCommandChange(
  rowId: string,
  next: string,
  knownFunctions: Set<string>
) {
  tableStore.recordRowEdit(
    rowId,
    'Change command',
    () => {
      tableStore.updateRow({
        rowId,
        updates: {
          [NodeColumns.command]: knownFunctions.has(next) ? next : undefined,
        } as Partial<TableRow>,
        skipValidationRules: false,
        saveImmediately: true,
      });
      tableStore.clearCellValidationError(rowId, NodeColumns.command);
    },
    NodeColumns.command
  );
}

// Exported (PSTUDIO-3624) so the Expanded Cell View panel reads a cell's
// validation error from the SAME source as the grid cell — the per-cell error
// indexes keyed by cellErrorKey(nodeNumber, columnId), gated by the agent-
// compiler mode. Returns the combined message (or undefined when the cell is
// error-free), so the panel can highlight the editor red identically to the grid.
export function getCellErrorMessage(
  rowId: string,
  columnId: string
): string | undefined {
  const rowNodeNumber = tableStore.getValue(rowId, NodeColumns.nodeNumber);
  const agentCompilerMode = readAgentCompilerMode(tableStore.featureFlags);
  // Address the cell by row id first (works when the node number is blank —
  // PSTUDIO-3991), then fall back to the node-number key.
  const idLookupKey = cellErrorKey(rowId, columnId);
  const numberLookupKey = cellErrorKey(
    rowNodeNumber as number | undefined,
    columnId
  );
  const resolveError = (
    byId: Map<string, { errorMessage: string }>,
    byNumber: Map<string, { errorMessage: string }>
  ) => (byId.get(idLookupKey) ?? byNumber.get(numberLookupKey))?.errorMessage;
  const apiErrorMessage =
    agentCompilerMode !== 'off'
      ? resolveError(tableStore.errorsByCellId, tableStore.errorsByCell)
      : undefined;
  const pifErrorMessage =
    agentCompilerMode !== 'bot_only'
      ? resolveError(tableStore.pifErrorsByCellId, tableStore.pifErrorsByCell)
      : undefined;
  // PSTUDIO-3990: the node-number errors land in BOTH indexes, so dedupe by
  // text — the divider must only ever separate DIFFERENT messages.
  return joinCellErrorMessages([pifErrorMessage, apiErrorMessage]);
}

// PSTUDIO-2821: a native cell editor must NOT close on incidental focus loss —
// clicking empty space outside the table, the scrollbar, or a far scroll that
// virtualizes the row out. It closes only on an EXPLICIT commit (Enter, Tab,
// click into another cell — focus lands inside the table scroller) or cancel
// (Esc). relatedTarget is the element RECEIVING focus: inside the table scroller
// ⇒ explicit move ⇒ commit; null/outside ⇒ incidental ⇒ keep the cell open (the
// draft is already in the store from each keystroke). Tab/Esc set
// cellCloseIntent first (they blur before focus lands, so relatedTarget is null).
function handleCellBlur(
  e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  cellId: string
) {
  // Only the ACTIVE editing cell drives commit/cancel. A merely-selected (but
  // not edited) control losing focus has nothing to commit.
  if (tableStore.editingCellId !== cellId) return;

  // Consume the close-intent single-shot: read once and clear immediately so a
  // 'commit'/'cancel' set for THIS blur can never survive to misfire on a later,
  // unrelated blur of another cell.
  const intent = tableStore.cellCloseIntent;
  tableStore.setCellCloseIntent(null);

  // Not in table edit mode (viewer/read-only) — just tear down, no write.
  if (!tableStore.isEditing()) {
    tableStore.cancelCellEditing();
    return;
  }

  if (intent === 'cancel') {
    tableStore.cancelCellEditing();
    return;
  }

  const next = e.relatedTarget;
  const movingInsideTable =
    next instanceof Node &&
    !!tableStore.tableScrollerRef &&
    tableStore.tableScrollerRef.contains(next);

  if (intent === 'commit' || movingInsideTable) {
    tableStore.commitCellEditing();
    return;
  }
  // Incidental focus loss → keep the cell open. The draft stays in the store.
}

// PSTUDIO-2821: Esc cancels the edit. Revert the visible value and flag the
// imminent blur as a cancel so handleCellBlur discards (instead of keeping the
// cell open like any other relatedTarget-null blur).
function handleCellEscape(
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  storedValue: unknown
) {
  if (e.nativeEvent.isComposing) return false;
  if (e.key !== 'Escape') return false;
  e.preventDefault();
  tableStore.setCellCloseIntent('cancel');
  e.currentTarget.value = stringifyValue(storedValue);
  e.currentTarget.blur();
  return true;
}

function GrowingCellEditor({
  columnId,
  index,
  display,
  readOnly,
  clamp,
  editorErrorAria,
  editKey,
  pointerEvents,
  cellId,
}: {
  columnId: string;
  index: number;
  display: string;
  readOnly: boolean;
  clamp: boolean;
  editorErrorAria: ReturnType<typeof resolveCellErrorAria>;
  editKey: string;
  pointerEvents: React.CSSProperties['pointerEvents'];
  cellId: string;
}) {
  const fitContentHeight = (ta: HTMLTextAreaElement) => {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  };
  return (
    <textarea
      key={editKey}
      test-id={`c-editor-table--${columnId}-${index}-textarea`}
      {...editorErrorAria}
      rows={1}
      defaultValue={display}
      readOnly={readOnly}
      onFocus={(e) => fitContentHeight(e.currentTarget)}
      onInput={(e) => {
        const ta = e.currentTarget;
        if (!columnStoresNewlines(columnId)) {
          const flat = draftWithoutStoredNewlines(ta.value);
          if (flat !== ta.value) {
            const caret = ta.selectionStart ?? flat.length;
            ta.value = flat;
            const next = Math.min(caret, flat.length);
            ta.selectionStart = ta.selectionEnd = next;
          }
        }
        fitContentHeight(ta);
        tableStore.setDraftCellValue(ta.value);
      }}
      onKeyDown={(e) => {
        if (handleCellEscape(e, display)) return;
        if (e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          if (!columnStoresNewlines(columnId)) return;
          e.preventDefault();
          e.stopPropagation();
          const ta = e.currentTarget;
          const start = ta.selectionStart;
          const end = ta.selectionEnd;
          ta.value =
            ta.value.substring(0, start) + '\n' + ta.value.substring(end);
          ta.selectionStart = ta.selectionEnd = start + 1;
          fitContentHeight(ta);
          tableStore.setDraftCellValue(ta.value);
        }
      }}
      onBlur={(e) => {
        const intentBefore = tableStore.cellCloseIntent;
        handleCellBlur(e, cellId);
        const close = classifyEditorWrapClose({
          stillEditingThisCell: tableStore.editingCellId === cellId,
          intent: intentBefore,
        });
        if (shouldCollapseEditorWrap(close)) {
          e.currentTarget.style.height = '100%';
        }
      }}
      style={{
        ...cellTextareaStyle,
        maxHeight: resolveEditorCellMaxHeight({ wrapEnabled: clamp }),
        pointerEvents,
      }}
    />
  );
}

// REACTIVITY: NativeCellValue must be an `observer`. It is rendered as a JSX
// child (<NativeCellValue/>), so its render runs in its own fiber — OUTSIDE the
// parent CellComponent observer's tracking scope — and none of its tableStore
// reads (rowMap value, editingCellId, isSelectedStartCell) would otherwise be
// tracked. The checkbox columns are the cell type this breaks hardest: they
// commit on change with no blur/selection transition afterwards, so with an
// untracked read the controlled <input checked={...}> never re-rendered after
// the synchronous store write — React snapped the box back to its last-rendered
// state, and the new value only appeared once the post-save validation pass
// touched an observable the parent tracks (errorsByCell). That is the
// "checkbox needs two clicks / only updates after the request" bug
// (PSTUDIO-3379).
const NativeCellValue = observer(function NativeCellValue({
  cell,
  rowId,
  index,
}: {
  cell: TableCell<TableRow, unknown>;
  rowId: string;
  index: number;
}) {
  const columnId = cell.column.id;
  // PSTUDIO-1433: JSON-capable columns (richAssetContent / parameterInput) are
  // edited in the side panel, so the grid inline editor must stay at the row's
  // normal height (no auto-grow) and must NOT steal focus from the panel on edit
  // entry — the cell should not change when the panel opens. analyticsEvent is
  // JSON-capable too and so reads true here, but its cell is the select below
  // (PSTUDIO-4784) and never reaches the textarea branch this flag serves.
  const isJsonInlineColumn = isJsonCapableColumn(columnId);
  // Read directly from the observable rowMap. Two earlier attempts had
  // problems:
  //   - cell.getValue() reads through TanStack Table's _valuesCache, which
  //     does not invalidate when we mutate the MobX row in place (so writes
  //     only became visible after a reload).
  //   - tableStore.getValue() is a `mobx-utils` computedFn that caches one
  //     entry per (rowId, columnId) pair *forever*; with ~25 columns × 5000
  //     rows that's ~125k cached derivations pinned in memory after a full
  //     scroll, ballooning the tab to 1+ GB.
  // Reading rowMap.get(rowId) subscribes to map changes, and accessing the
  // row's property subscribes to that property atom — both reactive, neither
  // cached. updateRow keeps rowMap in sync via `set(rowMap, rowId, updatedRow)`.
  const row = tableStore.rowMap.get(rowId);
  const value = row ? row[columnId as keyof TableRow] : undefined;
  const editing = tableStore.isEditing();

  // Native inputs/textareas should not catch clicks while the cell is
  // merely selected (otherwise `cell.click()` lands on the input directly
  // and focuses it — breaking the "single click selects, dblclick edits"
  // contract that the DS path enforced via the same gate). Allow pointer
  // events only when this cell is the active editing target.
  const cellId = `${rowId}-${columnId}`;
  // The SAME state the `<td>` carries, put on the control the user is actually
  // focused on. `aria-invalid` / `aria-describedby` on the cell describe the
  // gridcell; once an editor is mounted the focus is on the `<input>` or
  // `<textarea>` inside it, and a screen reader in forms mode reads the FIELD,
  // not its ancestor — so without this the message the cell announces is
  // exactly the one the user cannot hear while fixing it (WCAG 3.3.1).
  const editorErrorAria = resolveCellErrorAria(
    getCellErrorMessage(rowId, columnId),
    cellId
  );
  // Per-cell active edit target. Only the active cell (and only while the table
  // is in edit mode) mounts a live form control; every other cell renders a
  // lightweight <span> of its display value. This is the core DOM-weight
  // reduction: ~25 columns × ~50 rendered rows previously meant ~1,250 live
  // <input>/<textarea>/<select> mounted at rest. Requiring `editing` keeps the
  // non-editing/adding appearance read-only (matching the prior readOnly path)
  // so a stale editingCellId can never mount an editable control out of mode.
  const isActive = editing && tableStore.editingCellId === cellId;

  // PSTUDIO-2821: the lazily-mounted text/number controls are UNCONTROLLED
  // (`defaultValue`), so their `key` must change to re-mount and pick up an
  // externally-changed `value` — that is how a selected (read-only) cell stays
  // in sync with a paste/delete/cut/undo write. BUT keying on the value while
  // the cell is the ACTIVE edit target is a bug: any background row update (e.g.
  // the optimistic-concurrency 412 re-fetch fired by a prior save) changes
  // `value` → the key changes → React re-mounts the control mid-edit → the old
  // element's blur runs through `handleCellBlur` as a `movingInsideTable` commit
  // and clears editingRowId/Col BEFORE the draft is committed, so a following
  // Tab/Enter no-ops and the typed value is silently lost (flaky `fillRow` seed).
  // Fix: drop `value` from the key while THIS cell is actively edited — the draft
  // lives in the live DOM element and must not be re-mounted out from under the
  // user — and keep it in the key otherwise so the read-only view still refreshes.
  const editControlKey = (display: string) =>
    isActive ? `${rowId}:${columnId}` : `${rowId}:${columnId}:${display}`;
  const inputPointerEvents: React.CSSProperties['pointerEvents'] = isActive
    ? 'auto'
    : 'none';

  // Mount the live control (vs the lightweight view <span>) when the cell is
  // either being edited OR is the selected/focused cell. Keyboard navigation
  // (@pypestream/table-nav) implements the W3C grid pattern: Enter on a focused
  // cell steps focus INTO the cell's first focusable element, and Enter /
  // Shift+Enter from there move to the cell below / above. With only a <span>
  // there is nothing focusable to step into, so that whole chain dies and the
  // cell can't be entered or navigated past with the keyboard (PSTUDIO-3153).
  // Rendering the control for the selected cell restores the pre-perf behavior
  // (every text cell used to carry a live `<input readOnly={!editing}>`, so it
  // was focusable in view mode too) while still keeping all the non-selected
  // rows as cheap spans — at most one extra control is mounted. The control
  // stays read-only / pointer-events:none unless this is the active edit cell,
  // so the "single click selects, dblclick edits" contract is preserved. The
  // SELECT columns gate on `editing && isSelectedStartCell` instead, matching
  // their main behavior (a plain <span>, not a focusable <select>, in view mode).
  const showControl = isActive || tableStore.isSelectedStartCell(cellId);

  const textCellPresentation = () =>
    resolveTextCellPresentation({
      columnId,
      activelyEditing: isActive,
      wrapEnabled: isWrappableTextColumn(columnId)
        ? isColumnWrapEnabled(tableStore.columnWrapSettings, columnId)
        : false,
      jsonColumn: isJsonCapableColumn(columnId),
    });

  // Checkboxes — answerRequired, nluDisabled. Clickable, write-through.
  if (CHECKBOX_COLUMNS.has(columnId)) {
    // Rule-disabled = the store has greyed this cell out (e.g. nluDisabled when
    // "Disable Free Text" is on). Distinct from the !editing gate so we can key
    // the explanatory tooltip below off the rule, not off edit mode.
    const ruleDisabled = tableStore.isCellDisabled(
      rowId,
      columnId as keyof TableRow
    );
    const disabled = !editing || ruleDisabled;
    // PSTUDIO-2425: when NLU Disabled is rule-disabled (Free Text off), NLU is
    // not applicable, so render the box from the rule, not the stored value:
    // UNCHECKED for ordinary rows, but CHECKED for file_upload rows — the PIF
    // compiler requires nluDisabled=true whenever file_upload is specified.
    // writeBehaviors / the asset-type commit keep the stored flag in sync when
    // the rule is set in-session; this mask also covers rows that arrive with
    // a stale value from another source (DB / CSV import).
    const checked =
      columnId === NodeColumns.nluDisabled && ruleDisabled
        ? row?.[NodeColumns.richAssetType] ===
          Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload
        : Boolean(value);
    const checkbox = (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <input
          test-id={`c-editor-table--${columnId}-${index}-checkbox`}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) =>
            tableStore.writeCellValue(rowId, columnId, e.target.checked)
          }
          style={{
            // Lock the box from every side — browsers occasionally render
            // native checkboxes larger than `width/height` because of OS
            // platform defaults; setting min/max + box-sizing prevents that,
            // and `transform: scale` is a final fallback.
            width: '14px',
            height: '14px',
            minWidth: '14px',
            minHeight: '14px',
            maxWidth: '14px',
            maxHeight: '14px',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            transform: 'scale(0.95)',
            cursor: disabled ? 'default' : 'pointer',
            accentColor: 'var(--ps-colors-purple-500, #7d52f4)',
          }}
        />
      </span>
    );

    // PSTUDIO-2425: when NLU Disabled is greyed because Free Text is off, explain
    // why via a tooltip. Keep the lightweight native <input> (the perf refactor's
    // reason for not using ps-checkbox) rather than re-mounting a ps-checkbox per
    // row. This restores the tooltip the perf refactor dropped when it replaced
    // the column-def `cell` renderer with this hardcoded switch.
    if (columnId === NodeColumns.nluDisabled && ruleDisabled) {
      return (
        <Tooltip
          content="NLU doesn't apply when Free Text is disabled."
          reusePortal
          tooltipId={`nluDisabled-tooltip-${rowId}`}
        >
          {checkbox}
        </Tooltip>
      );
    }
    return checkbox;
  }

  // Node Type — show the label ("Decision"/"Action"), not the raw 'd'/'a'.
  // updateRowType also clears node-type-dependent cells (e.g. switching from
  // Decision -> Action wipes the decision-only fields). Wrap in
  // c-editor-table__node-type-cell so cell.scss's :has(...--a) selector flips
  // the td background to pale green for actions; the default pale-blue stays
  // for decisions.
  if (columnId === 'nodeType') {
    const raw = String(value ?? 'a');
    const normalized = raw.toLowerCase().startsWith('d') ? 'd' : 'a';
    const wrapperClass = `c-editor-table__node-type-cell c-editor-table__node-type-cell--${normalized}`;
    const label = normalized === 'd' ? 'Decision' : 'Action';
    // Lazy: render a dropdown-looking view (value + chevron) until this cell is
    // the focused cell, then mount the real <select>. Keeps the option lists out
    // of the DOM for the ~50 rendered rows while still reading as a dropdown.
    if (!(editing && tableStore.isSelectedStartCell(cellId))) {
      return (
        <div className={wrapperClass}>
          <CellViewSelect label={label} />
        </div>
      );
    }
    return (
      <div className={wrapperClass}>
        <select
          test-id={`c-editor-table--${columnId}-${index}-select`}
          className="c-editor-table__cell-select"
          value={normalized}
          onChange={(e) => {
            const next = e.target.value as TableRow['nodeType'];
            // Type switch can clear several type-dependent fields — record as
            // one undo/redo command (PSTUDIO-2154).
            tableStore.recordRowEdit(
              rowId,
              'Change node type',
              () => {
                tableStore.updateRowType({ rowId, newRowType: next });
                tableStore.clearCellValidationError(
                  rowId,
                  NodeColumns.nodeType
                );
              },
              NodeColumns.nodeType
            );
          }}
          style={cellInputStyle}
        >
          <option value="d">Decision</option>
          <option value="a">Action</option>
        </select>
      </div>
    );
  }

  // Read-only computed columns — render as static text.
  if (READ_ONLY_COLUMNS.has(columnId)) {
    return <span>{stringifyValue(value)}</span>;
  }

  // Node # — editable integer input. The original NodeNumberCell used
  // EditableTableCell with type="number"; reproduce the same semantics
  // here so the value lands in the store as a number, not a string.
  if (columnId === NodeColumns.nodeNumber) {
    const initial =
      typeof value === 'number' || typeof value === 'string'
        ? String(value)
        : '';
    if (!showControl) return <CellViewText text={initial} />;
    return (
      <input
        key={editControlKey(initial)}
        test-id={`c-editor-table--${columnId}-${index}-input`}
        {...editorErrorAria}
        type="number"
        defaultValue={initial}
        readOnly={!editing}
        onInput={(e) => tableStore.setDraftCellValue(e.currentTarget.value)}
        onKeyDown={(e) => handleCellEscape(e, value)}
        onBlur={(e) => handleCellBlur(e, cellId)}
        style={{ ...cellInputStyle, pointerEvents: inputPointerEvents }}
      />
    );
  }

  // Array-valued single-line inputs (nextNodes / output / variables /
  // whatsNext): join on the column's separator for display, split on it for
  // save. Matches the original cells' `onChangeConversion` / `renderValue`.
  if (ARRAY_INPUT_SEPARATORS[columnId]) {
    const sep = ARRAY_INPUT_SEPARATORS[columnId];
    // PSTUDIO-4081: a non-array value is SHOWN, not blanked — see arrayCellText.
    const initial = arrayCellText(value, sep);
    const presentation = textCellPresentation();
    if (
      !showControl ||
      presentation.control === 'wrapped-view' ||
      presentation.control === 'json-view'
    ) {
      const wrap =
        presentation.control === 'wrapped-view' ||
        (presentation.control === 'json-view' && presentation.wrap);
      return <CellViewText text={initial} wrap={wrap} />;
    }
    if (presentation.control === 'growing-editor') {
      return (
        <GrowingCellEditor
          columnId={columnId}
          index={index}
          display={initial}
          readOnly={!editing}
          clamp={presentation.clamp}
          editorErrorAria={editorErrorAria}
          editKey={editControlKey(initial)}
          pointerEvents={inputPointerEvents}
          cellId={cellId}
        />
      );
    }
    return (
      <input
        key={editControlKey(initial)}
        test-id={`c-editor-table--${columnId}-${index}-input`}
        {...editorErrorAria}
        type="text"
        defaultValue={initial}
        readOnly={!editing}
        onInput={(e) => tableStore.setDraftCellValue(e.currentTarget.value)}
        onKeyDown={(e) => handleCellEscape(e, value)}
        onBlur={(e) => handleCellBlur(e, cellId)}
        style={{ ...cellInputStyle, pointerEvents: inputPointerEvents }}
      />
    );
  }

  // Asset Type — single-select with labels. Side effects from the original
  // RichAssetTypeCell:
  //  1. updateRow with the new richAssetType + clear validation error
  //  2. if the new type has a default template in richAssetsConfig AND
  //     richAssetContent is empty, write the template (string or
  //     stringified JSON) and clear its validation error. Covers every
  //     asset type the config knows about (Buttons, Listpicker,
  //     Imagebutton, Datepicker, Secure, Carousel, Webview, Document,
  //     Image, Video, Location, FileUpload, PersistentMenu, Star, Png,
  //     QuickReply, Button, Audio); 'none' has no default and is skipped.
  if (columnId === 'richAssetType') {
    const current = typeof value === 'string' ? value : '';
    const currentLabel = current
      ? (ASSET_LABELS[current as keyof typeof ASSET_LABELS] ?? current)
      : '';
    // Lazy: dropdown-looking view until focused, then mount the real <select>
    // with its full option list.
    if (!(editing && tableStore.isSelectedStartCell(cellId))) {
      return <CellViewSelect label={currentLabel} />;
    }
    const options = getAssetTypeList();
    return (
      <select
        test-id={`c-editor-table--${columnId}-${index}-select`}
        className="c-editor-table__cell-select"
        value={current}
        onChange={(e) => commitAssetTypeChange(rowId, e.target.value)}
        style={cellInputStyle}
      >
        {!options.some((o) => o.type === current) && (
          <option value={current}>{currentLabel || '—'}</option>
        )}
        {options.map(({ type, label }) => (
          <option key={type} value={type}>
            {label}
          </option>
        ))}
      </select>
    );
  }

  // Behaviors — opt-out of the native baseline and use the design-system
  // multi-select. The native <select multiple> needed cmd/ctrl-click for
  // toggle, and a checkbox-list rewrite still didn't match design. Keep
  // this column on the DS component and measure whether its render cost
  // matters at scale.
  if (columnId === 'behaviors') {
    return (
      <NodeBehaviorsMultiSelect
        rowId={rowId}
        columnId={columnId}
        onEdit={(values) => writeBehaviors(rowId, values)}
      />
    );
  }

  // Command — built-in standard functions + uploaded action files (Python
  // scripts under services/) + whatever value is currently saved. Side
  // effects from the original NodeCommandSelect:
  //  - if the chosen value is in the known list (standard or uploaded),
  //    save it. Otherwise save `undefined` so the cell clears.
  //  - clear validation error after the write.
  if (columnId === 'command') {
    return (
      <NodeCommandSelectNative rowId={rowId} index={index} value={value} />
    );
  }

  // Analytics Events (PSTUDIO-4784) — a select of the Custom Events Library events that also
  // takes a name typed into it. Placed before the textarea/JSON branch below on
  // purpose: this column used to be a read-only view whose editing lived only in
  // the side panel; it is a SELECT column now (SELECT_COLUMNS), so the store's
  // edit-entry methods leave the cell to this control. A cell holding more than a
  // name (send, variables, a list) is still written in the panel's JSON editor.
  if (columnId === NodeColumns.analyticsEvent) {
    return (
      <AnalyticsEventCellSelect rowId={rowId} index={index} value={value} />
    );
  }

  // Long-text / structured columns — textarea, save on blur.
  // rows={1}: without this, browsers default to rows="2" and the textarea's
  // intrinsic height makes every row ~40px+ tall instead of the ~32px the
  // design-system text cells produced.
  // On focus, expand height to fit content (Google-Sheets-style auto-grow).
  // The grown height collapses when the edit session ends, not on an
  // incidental blur (PSTUDIO-4087).
  if (TEXTAREA_COLUMNS.has(columnId)) {
    // Message renders the read-only view span whenever it is NOT being ACTIVELY
    // edited — including when merely SELECTED — so selecting a cell never changes
    // its height or clip/wrap behaviour; only entering edit mode mounts the
    // auto-grow <textarea>. The span stays in sync with paste/cut/delete/undo
    // writes via the observable rowMap (reactive read), so it needs none of the
    // uncontrolled-textarea key-remount dance (PSTUDIO-2821).
    //
    // Keyboard-nav note (re PSTUDIO-3153): 3153 made a selected text/textarea cell
    // mount the live control because "grid nav steps focus INTO a cell's first
    // focusable element". Rendering a bare <span> for a selected Message cell does
    // NOT regress that: the data <td> itself is the focus target (tabIndex={-1} +
    // role="gridcell"), and edit entry (Enter / F2 / type-to-edit / dblclick) is
    // driven by the PSTUDIO-2821 capture-phase handler in table.tsx off the <td>
    // (getColumnEditKind('message')==='text' → lazy-mount via pendingEditCellId),
    // NOT by a pre-mounted focusable child. Same pattern the SELECT columns already
    // use (CellViewSelect). Verified live: Enter on a selected wrapped Message cell
    // opens edit. Do NOT "restore" the control here.
    // PSTUDIO-1433: a JSON-capable cell (richAssetContent / parameterInput) must
    // NOT pre-mount an editable <textarea> while it is only
    // SELECTED — same reasoning as Message above. `editing` here is
    // tableStore.isEditing() (the whole solution's draft mode), so
    // `readOnly={!editing}` is false for a merely-selected cell, leaving a live
    // editable textarea as the cell's first focusable child. table-nav's W3C grid
    // pattern then steps Enter straight INTO that textarea (inline edit), bypassing
    // the capture-phase toggleCellInput in table.tsx — so editingColumnId never
    // changes, the auto-open reaction never fires, and focus lands in the grid
    // textarea instead of the panel's CodeEditor.
    //
    // A JSON-capable cell (richAssetContent / parameterInput) is
    // edited EXCLUSIVELY in the side panel's CodeEditor (product decision), so it
    // must NEVER mount an inline <textarea> at all — not while selected AND not
    // while active. Any inline textarea would (a) be a focusable child table-nav
    // steps Enter into (stealing focus from the panel) and (b) let the cell edit
    // in two places at once. Rendering the read-only view span for every JSON cell
    // state routes Enter/F2/type-to-edit/double-click through the capture handler
    // (getColumnEditKind==='text' → toggleCellInput → editingColumnId → auto-open
    // + panel autofocus), and the row never balloons (Pt.5) because there is no
    // auto-growing textarea. Message keeps its original selected-only view span.
    const presentation = textCellPresentation();
    const isViewWhileSelected =
      (columnId === NodeColumns.message && !isActive) ||
      isJsonInlineColumn ||
      presentation.control === 'wrapped-view';
    if (!showControl || isViewWhileSelected) {
      const wrap =
        presentation.control === 'wrapped-view' ||
        (presentation.control === 'json-view' && presentation.wrap);
      return <CellViewText text={stringifyValue(value)} wrap={wrap} />;
    }
    const clamp =
      presentation.control === 'growing-editor' && presentation.clamp;
    return (
      <GrowingCellEditor
        columnId={columnId}
        index={index}
        display={stringifyValue(value)}
        readOnly={!editing}
        clamp={clamp}
        editorErrorAria={editorErrorAria}
        editKey={editControlKey(stringifyValue(value))}
        pointerEvents={inputPointerEvents}
        cellId={cellId}
      />
    );
  }

  // Default — single-line input, except an active edit, which grows a
  // wrapping editor and stays grown until the edit is submitted or cancelled.
  const presentation = textCellPresentation();
  if (!showControl || presentation.control === 'wrapped-view') {
    return (
      <CellViewText
        text={stringifyValue(value)}
        wrap={presentation.control === 'wrapped-view'}
      />
    );
  }
  if (presentation.control === 'growing-editor') {
    return (
      <GrowingCellEditor
        columnId={columnId}
        index={index}
        display={stringifyValue(value)}
        readOnly={!editing}
        clamp={presentation.clamp}
        editorErrorAria={editorErrorAria}
        editKey={editControlKey(stringifyValue(value))}
        pointerEvents={inputPointerEvents}
        cellId={cellId}
      />
    );
  }
  return (
    <input
      key={editControlKey(stringifyValue(value))}
      test-id={`c-editor-table--${columnId}-${index}-input`}
      type="text"
      defaultValue={stringifyValue(value)}
      readOnly={!editing}
      onInput={(e) => tableStore.setDraftCellValue(e.currentTarget.value)}
      onKeyDown={(e) => handleCellEscape(e, value)}
      onBlur={(e) => handleCellBlur(e, cellId)}
      style={{ ...cellInputStyle, pointerEvents: inputPointerEvents }}
    />
  );
});
// Suffix icons for the nodeNumber cell — purple "stars" for an NL Entry node
// (intent === 'out_of_scope') and a "target" for a Task Node (settings.taskId
// matches a task on the draft solution). Restored from the original
// NodeNumberCell in table.tsx; the native cell renderer above only outputs
// the number itself, so without this the indicators were missing.
const NodeNumberSuffixes = observer(({ rowId }: { rowId: string }) => {
  const row = tableStore.rowMap.get(rowId);
  const intent = row?.intent;
  const taskId = row?.settings?.taskId;
  const { draftVersionSolutionData } = useProStudioCtxSelector((ctx) => ({
    draftVersionSolutionData: ctx.draftVersionSolutionData,
  }));
  const isTaskNode =
    !!taskId &&
    !!draftVersionSolutionData?.tasks?.some((task) => task.id === taskId);

  return (
    <>
      {intent === 'out_of_scope' && (
        <Tooltip
          test-id="c-editor-table-td-nlu-entry-tooltip"
          content="Natural Language (NL) Entry"
          offset={2}
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="stars" color="purple" />
          </div>
        </Tooltip>
      )}
      {isTaskNode && (
        <Tooltip
          test-id="c-editor-table-td-task-node-tooltip"
          content="Task Node"
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="target" color="purple" />
          </div>
        </Tooltip>
      )}
    </>
  );
});

// Stable error-tooltip portal (PSTUDIO-2321). The createPortal call is
// UNCONDITIONAL so its fiber never changes sibling position as errorMessage
// toggles or the virtualized cell remounts — that interleaving is what made
// React's document.body.removeChild target a stale node and throw
// "The node to be removed is not a child of this node." The <ps-popover-tooltip>
// stays mounted; visibility is driven by the reactive `active` prop.
function CellErrorTooltip({
  cellId,
  errorMessage,
  active,
}: {
  cellId: string;
  errorMessage: string | undefined;
  active: boolean;
}) {
  return createPortal(
    <>
      <PopoverTooltip
        placement="top"
        trigger={cellId}
        active={Boolean(errorMessage) && active}
        omitPortal
        omit-portal
        keepMounted
      >
        {errorMessage ?? ''}
      </PopoverTooltip>
      {/* The cell's `aria-describedby` target (see cellErrorDescriptionStyle).
          Rendered UNCONDITIONALLY, for the same reason the portal itself is: a
          child that appears and disappears moves its sibling's fiber position,
          which is what made React remove a stale node from document.body. */}
      <span
        id={cellErrorDescriptionId(cellId)}
        style={cellErrorDescriptionStyle}
      >
        {errorMessage ?? ''}
      </span>
    </>,
    document.body
  );
}

// Command cell renderer — needs the user-uploaded action files from the
// draft version (XState ctx) via a hook, so it lives in its own observer
// component instead of inline in NativeCellValue's switch.
// Mirrors the original NodeCommandSelect's data shape: the dynamic native
// actionNodeNames (system + library) merged with files under services/ in the
// draft, then sorted.
const NodeCommandSelectNative = observer(
  ({
    rowId,
    index,
    value,
  }: {
    rowId: string;
    index: number;
    value: unknown;
  }) => {
    const editing = tableStore.isEditing();
    const current = typeof value === 'string' ? value : '';
    const cellId = `${rowId}-${NodeColumns.command}`;

    // Hook must run unconditionally, before the lazy-view early return.
    const { draftFiles } = useProStudioCtxSelector((ctx) => ({
      draftFiles: ctx.draft?.files ?? [],
    }));

    // Lazy: dropdown-looking view until focused, then mount the real <select>.
    if (!(editing && tableStore.isSelectedStartCell(cellId))) {
      return <CellViewSelect label={current} />;
    }

    const knownFunctions = new Set<string>(
      buildScriptFileNames(draftFiles, tableStore.actionNodeNames)
    );
    const optionSet = new Set<string>(knownFunctions);
    if (current) optionSet.add(current);
    const options = [...optionSet].sort((a, b) => a.localeCompare(b));

    return (
      <select
        test-id={`c-editor-table--command-${index}-select`}
        className="c-editor-table__cell-select"
        value={current}
        onChange={(e) =>
          commitCommandChange(rowId, e.target.value, knownFunctions)
        }
        style={cellInputStyle}
      >
        {!current && <option value="">—</option>}
        {options.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    );
  }
);

// Analytics Events cell renderer (PSTUDIO-4784). Lazy like the other SELECT
// cells: the dropdown-looking view until the cell is the selected start cell of
// an editable table, then the real design-system select. It writes through
// `commitAnalyticsEventChoice` — the same helper the panel's select uses — so a
// pick or a typed name is one recorded edit, undoable in one step, from either.
const AnalyticsEventCellSelect = observer(
  ({
    rowId,
    index,
    value,
  }: {
    rowId: string;
    index: number;
    value: unknown;
  }) => {
    const editing = tableStore.isEditing();
    const cellId = `${rowId}-${NodeColumns.analyticsEvent}`;
    const label = analyticsEventCellLabel(value);

    if (!(editing && tableStore.isSelectedStartCell(cellId))) {
      return <CellViewSelect label={label} />;
    }

    return (
      <AnalyticsEventSelect
        testId={`c-editor-table--analyticsEvent-${index}-select`}
        currentName={label}
        size="small"
        disabled={tableStore.isCellDisabled(rowId, NodeColumns.analyticsEvent)}
        errorMessage={getCellErrorMessage(rowId, NodeColumns.analyticsEvent)}
        onChoose={(name) => commitAnalyticsEventChoice(rowId, name)}
      />
    );
  }
);

interface CellProps {
  cell: TableCell<TableRow, unknown>;
  rowId: string;
  hidden?: boolean;
}

const CellComponent = (
  { cell, rowId, hidden }: CellProps,
  ref: React.Ref<HTMLTableCellElement>
) => {
  const cellId = `${rowId}-${cell.column.id}`;
  const coworkerFocus = tableStore.coworkerFocusByCellId.get(cellId);
  const coworkerFocusLabel = coworkerFocus?.label ?? null;
  const coworkerFocusColorIndex = coworkerFocus?.colorIndex ?? null;
  // PSTUDIO-2372 presence: translucent tint for cells inside a coworker's
  // selected range (the solid border + label stay on their anchor cell only).
  const coworkerRangeColorIndex =
    tableStore.coworkerRangeByCellId.get(cellId) ?? null;

  // O(1) lookup into the store-side per-cell error indexes (shared helper —
  // the Expanded Cell View panel reads the exact same source).
  const errorMessage = getCellErrorMessage(rowId, cell.column.id);

  const { nodeType } = tableStore.getRowById(rowId);

  const [contextMenuState, setContextMenuState] = useState<
    'closed' | 'closing' | 'opened'
  >('closed');

  // PSTUDIO-3164 (Leo follow-up): which menu the right-click opens follows the
  // active selection kind — 'cell' for a cell selection (per-cell value menu),
  // 'row' otherwise (the shared row menu).
  const [menuKind, setMenuKind] = useState<'row' | 'cell'>('row');

  const [isTooltipInitiallyActive, setIsTooltipInitialActive] = useState(false);

  // const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const { getRowIndex, isCellSelected, isSelectedStartCell } = tableStore;
  const index = getRowIndex(rowId);

  // Compute pinning styles once per render (was called up to 4× per cell).
  const pinning = getCommonPinningStyles(cell.column);

  const isSelected = isCellSelected(cellId);

  const isStarterCell = isSelectedStartCell(cellId);

  const isCellCopiedOrCut = tableStore.isCellCopiedOrCut(cellId);
  const isCellCut = isCellCopiedOrCut && tableStore.isCellClipboardCut;

  const cellRef = useRef<HTMLTableCellElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => cellRef.current!, [cellRef.current]);

  // Edit-mode focus for the lazily-mounted text columns: an inactive
  // text/textarea/number cell renders only a <span>; when toggleCellInput sets
  // editingCellId/pendingEditCellId for it, the control mounts on the next
  // commit and this layout-effect (running after that commit) focuses it and
  // places the caret at the end. Select/checkbox/behaviors columns are always
  // rendered and are not driven by pendingEditCellId.
  const { pendingEditCellId } = tableStore;
  useLayoutEffect(() => {
    if (pendingEditCellId !== cellId || !cellRef.current) return;
    // PSTUDIO-1433: a JSON-capable cell is edited in the side panel, which
    // auto-opens and takes focus into its CodeEditor on edit entry. Do NOT focus
    // the grid inline editor here — it would steal focus back from the panel.
    // Clear the pending flag (so it doesn't linger) and bail.
    if (isJsonCapableColumn(cell.column.id)) {
      tableStore.pendingEditCellId = null;
      return;
    }
    const input = cellRef.current.querySelector<
      HTMLInputElement | HTMLTextAreaElement
    >('input, textarea');
    if (!input) return;
    tableStore.pendingEditCellId = null;
    // PSTUDIO-4026: do not let the browser auto-scroll the table on focus; bring
    // the cell into view explicitly only if it is actually off-screen.
    input.focus({ preventScroll: true });
    // PSTUDIO-4846: a type-to-edit entry REPLACES the stored text with what is
    // typed, and the first character is injected one frame later. A key that
    // lands in this control before that frame must replace the stored text too,
    // not append to it, so the frame can put the seed character in front of it.
    // Only a type-to-edit entry has a seed in flight for this cell; dblclick /
    // F2 / Enter keep the caret at the end below.
    selectStoredTextForTypeToEdit(input, tableStore.typedSeedInFlight, cellId);
    scrollCellIntoViewIfNeeded(cellRef.current, tableStore.tableScrollerRef);
    requestAnimationFrame(() => {
      if (
        input instanceof HTMLInputElement &&
        (input.type === 'number' || input.type === 'checkbox')
      ) {
        return;
      }
      const len = input.value?.length ?? 0;
      input.setSelectionRange(len, len);
    });
    // cell.column.id is stable for the life of this cell instance (cellId already
    // encodes it: `${rowId}-${columnId}`), so it never re-triggers on its own.
  }, [pendingEditCellId, cellId, cell.column.id]);

  // PSTUDIO-2821: when a row that is mid-edit is scrolled back into the
  // virtualized window it remounts FRESH (react-virtual unmounts rows beyond
  // the overscan/window). Restore the in-progress draft into the freshly-mounted
  // control and re-focus, so a far scroll never loses the edit. Mount-only
  // (deps []): a click-outside that does NOT unmount must not re-grab focus, so
  // this runs solely on (re)mount, never on every editingCellId change.
  // PSTUDIO-2821 coverage note: the far-scroll -> unmount -> remount path is NOT
  // e2e-covered -- forcing a react-virtual unmount of the editing row needs a
  // 100+ row fixture (small e2e workspaces never virtualize it out, so the test
  // would be vacuous/flaky). Draft persistence is unit-covered in
  // cell-editing-commit-cancel.test.ts; this rehydrate wiring is QA'd manually.
  useLayoutEffect(() => {
    if (tableStore.editingCellId !== cellId || !cellRef.current) return;
    // PSTUDIO-1433: a JSON-capable cell is edited in the side panel, which owns
    // focus (its CodeEditor). On a far-scroll remount of the editing row do NOT
    // pull focus back into the grid inline editor — it would steal focus from
    // the panel. Mirrors the pending-edit effect's skip above.
    if (isJsonCapableColumn(cell.column.id)) return;
    const input = cellRef.current.querySelector<
      HTMLInputElement | HTMLTextAreaElement
    >('input, textarea');
    if (!input) return;
    if (tableStore.draftCellValue != null) {
      input.value = tableStore.draftCellValue;
    }
    // Do NOT steal focus the user intentionally moved OUTSIDE the table (e.g.
    // a side panel) while this cell was left open by an incidental blur. On the
    // far-scroll remount the editor was unmounted so focus fell to <body> — that
    // case still refocuses. Only skip when focus is on a real element outside
    // the table scroller.
    const active = document.activeElement;
    const focusIsElsewhere =
      active instanceof HTMLElement &&
      active !== document.body &&
      !!tableStore.tableScrollerRef &&
      !tableStore.tableScrollerRef.contains(active);
    if (focusIsElsewhere) return;
    // PSTUDIO-4026: same as the pending-edit effect — focus without auto-scroll,
    // then bring the cell into view explicitly only when it is off-screen (a
    // far-scroll remount can land the editing row just outside the viewport).
    input.focus({ preventScroll: true });
    scrollCellIntoViewIfNeeded(cellRef.current, tableStore.tableScrollerRef);
    const len = input.value?.length ?? 0;
    if (!(input instanceof HTMLInputElement && input.type === 'number')) {
      input.setSelectionRange(len, len);
    }
    // Mount-only: cellId is stable per cell instance; tableStore reads are
    // intentional one-shot reads, not reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIndexCell = cell.column.id === 'index';

  /**
   * externally reset the final context popover state back to closed to
   * avoid the component destroying itself
   */
  // PSTUDIO-1433 (crash fix): the SAME delayed close the row menu has
  // (`draggable-row.tsx`), and the same hazard — unmounting a menu that has been
  // reopened in the meantime crashes the page, because `ps-dropdown` moves
  // ITSELF into the popover portal while open and React's `removeChild` then
  // runs against the wrong parent (`NotFoundError`).
  //
  // The old guard read `contextMenuState` from the effect's CLOSURE, so it saw
  // the value captured when the timer was scheduled — still `'closing'` even
  // after the menu had reopened — and forced `'closed'` anyway. Reading the live
  // state through the functional updater is what makes the guard real; the
  // timer is also cancelled now, and the effect no longer schedules one for
  // every state change.
  useEffect(() => {
    if (contextMenuState !== 'closing') return undefined;

    const timeoutId = setTimeout(() => {
      setContextMenuState(resolveDelayedContextMenuClose);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [contextMenuState]);

  // PSTUDIO-3162 family: `data-popover-open` paints the cell while its menu is
  // up. React does not manage the attribute, so it is cleared from the STATE
  // rather than from whichever close path happened to run — clearing it in
  // `onRequestClose` alone dropped the marker on a menu that then reopened.
  useEffect(() => {
    if (contextMenuState === 'closed') {
      cellRef.current?.removeAttribute('data-popover-open');
    }
  }, [contextMenuState]);

  // PSTUDIO-1433 (crash fix, second entrance): the table is virtualized, so
  // scrolling far enough unmounts this row while its menu is still open — and
  // unmounting a portal-hosted `ps-dropdown` is the `removeChild` crash. Close
  // on scroll, which is what a menu should do anyway.
  const closeMenuOnScroll = useCallback(() => {
    setContextMenuState((current) =>
      current === 'closed' ? current : 'closing'
    );
  }, []);
  // React to the table-level close signal instead of subscribing to scroll per
  // cell/row — see `requestCloseAllContextMenus`. Reading the nonce inside an
  // observer component makes this a MobX subscription, which costs nothing
  // until the value actually changes.
  // Keep the store's open-menu count in step with this menu's own state, so
  // the table can subscribe to scroll only while some menu is actually open.
  const menuIsOpen = contextMenuState !== 'closed';
  useEffect(() => {
    if (!menuIsOpen) return undefined;
    tableStore.registerOpenContextMenu();
    return () => tableStore.unregisterOpenContextMenu();
  }, [menuIsOpen]);

  // Subscribe to the close signal ONLY while this menu is open. Reading the
  // observable unconditionally subscribed every rendered cell (~24 columns ×
  // ~50 rows ≈ 1200 of them) to a nonce that concerns at most one — so a single
  // scroll re-rendered the whole grid. MobX subscribes by what a render
  // actually reads, and opening the menu re-renders this cell through its own
  // local state, so the subscription appears exactly where it is needed
  // (PSTUDIO-1433).
  const closeNonce = menuIsOpen ? tableStore.contextMenuCloseNonce : 0;
  // The nonce is monotonic and never resets, so "non-zero" cannot mean "a signal
  // arrived for me" — see `resolveMenuCloseSignal`, which owns that reasoning
  // and is shared with the row menu.
  const nonceLatchRef = useRef<number | null>(null);
  useEffect(() => {
    const { latchedNonce, shouldClose } = resolveMenuCloseSignal({
      latchedNonce: nonceLatchRef.current,
      isOpen: menuIsOpen,
      nonce: closeNonce,
    });
    nonceLatchRef.current = latchedNonce;
    if (shouldClose) closeMenuOnScroll();
    // `closeMenuOnScroll` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuIsOpen, closeNonce]);

  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     // Check for common navigation keys like Tab, arrow keys, Enter
  //     if (
  //       e.key === 'Tab' ||
  //       e.key === 'ArrowUp' ||
  //       e.key === 'ArrowDown' ||
  //       e.key === 'ArrowLeft' ||
  //       e.key === 'ArrowRight'
  //     ) {
  //       setIsKeyboardNavigating(true);
  //     }
  //   };

  //   const handleMouseDown = () => {
  //     setIsKeyboardNavigating(false);
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   document.addEventListener('mousedown', handleMouseDown);

  //   return () => {
  //     document.removeEventListener('keydown', handleKeyDown);
  //     document.removeEventListener('mousedown', handleMouseDown);
  //   };
  // }, []);

  const handleFocus = () => {
    // PSTUDIO-4415 (S55): focus reaching a grid cell does not move the document
    // selection, and while the Expanded Cell View is open that selection can
    // still be in its JSON editor — where Chrome then delivers Cmd+V, bypassing
    // the grid paste. Drop it so the paste reaches this cell.
    releasePanelSelection();
    emitOnce(() => {
      // PSTUDIO-3164: preserve an existing multi-row selection that already
      // includes THIS row. A right-click focuses the cell — directly on
      // mousedown, and again when the row context-menu popover focuses its
      // trigger <td> — and `handleFocus` used to UNCONDITIONALLY clear the row
      // selection, wiping it before `onContextMenu` could read it. The menu
      // then opened on a single row and "Group" never rendered. Mirror the
      // `onContextMenu` / `handleIndexCellClick` guard: only clear when the
      // focused row is NOT part of the current selection. A plain LEFT click
      // still drops row selection via `onClick` (deselectAllRows there), so
      // cell selection on click is unaffected.
      if (
        !tableStore.isViewer &&
        !(tableStore.selectedRows.size >= 1 && tableStore.isSelectedRow(rowId))
      ) {
        tableStore.deselectAllRows();
      }

      setIsTooltipInitialActive(true);

      // PSTUDIO-2372: skip the keyboard-nav reset during a Shift+Arrow range
      // extension — its focus move must NOT reset the selection anchor, or the
      // range collapses to the focused cell. isShiftArrowNav is set by the
      // capture-phase keydown in table.tsx (before table-nav moves focus) and
      // is false for Shift+Tab / Shift+Enter, which keep re-anchoring here.
      if (
        !tableStore.isViewer &&
        tableStore.isKeyboardNav &&
        !tableStore.isShiftArrowNav
      ) {
        tableStore.deselectAllCells();
        tableStore.selectCell({
          cellId,
          columnId: cell.column.id as NodeColumns,
          rowId,
        });
        tableStore.setSelectedStartCell({
          cellId,
          columnId: cell.column.id as NodeColumns,
          rowId,
        });
        // console.log('Focused by keyboard!');
        // Apply keyboard-specific logic or styling
      } else {
        // console.log('Focused by mouse click!');
        // Apply mouse-specific logic or styling
      }
    });
  };

  const handleBlur = (e?: React.FocusEvent<HTMLTableCellElement>) => {
    setIsTooltipInitialActive(false);

    // PSTUDIO-1433: focus moving INTO the Expanded Cell View panel is NOT a
    // keyboard cell-to-cell navigation — the panel edits THIS very cell (auto-open
    // on entering a JSON-capable cell moves focus into the panel's CodeEditor). The
    // keyboard-nav branch below deselects the cell on blur (correct when Enter/Tab
    // moves to another cell); doing so here would clear selectedCells, which blanks
    // the panel it just opened (isSelectedCellRenderable() needs exactly one
    // selected cell). So when focus lands inside the panel, keep the selection.
    const next = (e?.relatedTarget ?? document.activeElement) as Element | null;
    const focusEnteringPanel =
      next instanceof Element &&
      !!next.closest('[class*="c-expanded-cell-view"], ps-code-editor');
    if (focusEnteringPanel) return;

    // PSTUDIO-2372: keep the anchor + ranged cells during a Shift+Arrow range
    // extension so the keyboard focus move can extend the range (see
    // handleFocus). Shift+Tab / Shift+Enter still clear here.
    if (tableStore.isKeyboardNav && !tableStore.isShiftArrowNav) {
      emitOnce(() => {
        tableStore.deselectCell(cellId);
        tableStore.clearSelectedStartCell();
      });
    }
  };

  const cellContent = (
    <div
      test-id={`c-editor-table--${cell.column.id}-${index}-${tableStore.isEditing() ? 'edit' : 'view'}-mode-inner`}
      className="c-editor-cell__inner"
      style={{
        position: 'relative',
        zIndex: 1,
        height: '100%',
        display: 'flex',
        pointerEvents:
          errorMessage ||
          (isSelected &&
            (tableStore.isMetaKeyPressed === false ||
              (tableStore.isCellBeingEdited &&
                tableStore.isMetaKeyPressed === true)))
            ? 'auto'
            : 'none',
      }}
      ref={innerRef}
    >
      <NativeCellValue cell={cell} rowId={rowId} index={index} />
      {cell.column.id === NodeColumns.nodeNumber && (
        <NodeNumberSuffixes rowId={rowId} />
      )}
    </div>
  );

  if (nodeTypeDependentCells[nodeType].includes(cell.column.id as NodeColumns))
    return (
      <td
        data-hidden={hidden}
        data-disabled
        test-id={`c-editor-table--${cell.column.id}-td-${index}`}
        key={cell.id}
        id={`${cellId}-td`}
        role="gridcell"
        tabIndex={-1}
        data-row-id={rowId}
        data-cell-id={cell.column.id}
        className={`c-editor-table-td c-editor-table-td--cleared c-editor-table-td--${cell.column.id} ${pinning.classNames} ${isStarterCell ? 'is-selection-start' : ''} ${isCellCopiedOrCut ? 'c-editor-table-td--copied' : ''} ${isCellCut ? 'c-editor-table-td--cut' : ''}`}
        style={{
          ...pinning.styles,
          ...(hidden ? { display: 'none' } : {}),
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      ></td>
    );

  return (
    <td
      data-hidden={hidden}
      test-id={`c-editor-table--${cell.column.id}-td-${index}`}
      key={cell.id}
      ref={cellRef}
      // tabIndex={0}
      id={cellId}
      role="gridcell"
      tabIndex={-1}
      data-row-id={rowId}
      data-cell-id={cell.column.id}
      onMouseDown={(event) => {
        if (event.detail >= 2) {
          // Project Bobcat: the gesture turned out to be a double-click, so
          // abandon the property editor's deferred open. A double-click edits
          // the field and must not move the panel (product decision, Gary
          // 2026-09-09) — otherwise the panel opens between the two clicks and
          // the grid reflows out from under the second one. This mousedown
          // fires BEFORE the second click's onClick, which is what makes the
          // cancel land in time. No-op when nothing is pending.
          tableStore.cancelPendingPanelOpen();
        }

        if (
          event.detail >= 2 &&
          !event.metaKey &&
          tableStore.editingCellId !== cellId
        ) {
          event.preventDefault();
        }
      }}
      onClick={(event) => {
        if (tableStore.isViewer) return;

        // PSTUDIO-2821: clicking into a DIFFERENT cell commits the currently
        // open editor — even when it lost focus to an incidental blur
        // (click-outside / scrollbar) and so won't fire its own blur-commit.
        // Idempotent and guarded to a different cell, so re-clicking the open
        // cell to reposition the caret keeps it open.
        if (tableStore.editingCellId && tableStore.editingCellId !== cellId) {
          tableStore.commitCellEditing();
        }

        // auto-close any open popovers
        if (contextMenuState !== 'closed') {
          setContextMenuState('closing');
        }

        // Project Bobcat: a plain click opens the property editor. Flag-gated
        // and off by default — the store decides, so this stays a bare call in
        // the hot path (see `openPropertyPanelFromClick`). Which clicks
        // qualify lives in `shouldOpenPropertyEditorOnClick`, beside
        // `shouldToggleExpandedCellView`, so it is testable without rendering
        // a cell.
        //
        // Deliberately BEFORE `emitOnce`: the selection branches inside it
        // early-return when re-clicking an already-selected single cell
        // (`isSelected && selectedCells.length === 1`), and that click should
        // still open the panel.
        if (shouldOpenPropertyEditorOnClick(event)) {
          tableStore.openPropertyPanelFromClick();
        }

        emitOnce(() => {
          if (!event.shiftKey) {
            if (event.metaKey && isStarterCell) {
              tableStore.clearSelectedStartCell();
              tableStore.deselectCell(cellId);
            } else {
              tableStore.setSelectedStartCell({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }
          }

          if (!isIndexCell) {
            tableStore.deselectAllRows();
          }

          if (event.metaKey && isSelected && !tableStore.isCellBeingEdited) {
            tableStore.deselectCell(cellId);
          } else if (event.metaKey && !isSelected) {
            tableStore.selectCell({
              cellId,
              rowId,
              columnId: cell.column.id as NodeColumns,
            });
          } else if (!event.metaKey) {
            if (isSelected && tableStore.selectedCells.length === 1) return;
            tableStore.deselectAllCells(cellId);

            if (!isSelected && event.shiftKey) {
              tableStore.selectCell({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }

            if (event.shiftKey && tableStore.selectedStartCell) {
              tableStore.updateRangeSelection({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }
          }
        });
      }}
      onDoubleClick={(event) => {
        if (tableStore.isViewer) return;

        if (event.metaKey) {
          if (isSelected) {
            tableStore.deselectCell(cellId);
          } else {
            tableStore.selectCell({
              cellId,
              rowId,
              columnId: cell.column.id as NodeColumns,
            });
          }
        } else {
          tableStore.selectCell({
            cellId,
            rowId,
            columnId: cell.column.id as NodeColumns,
          });
          tableStore.deselectAllCells(cellId);

          if (cellRef.current && tableStore.editingCellId !== cellId) {
            tableStore.toggleCellInput(
              cellRef.current,
              rowId,
              cell.column.id as NodeColumns
            );
          }
        }
      }}
      onContextMenu={(e) => {
        if (tableStore.isViewer) return;
        e.preventDefault();
        e.stopPropagation();

        // PSTUDIO-3164 (Leo follow-up): the menu follows the SELECTION kind.
        // When a CELL selection is active, keep the per-cell value menu scoped
        // to the right-clicked cell; otherwise (rows selected, or nothing)
        // open the row menu — the original PSTUDIO-3164 behaviour.
        if (
          resolveNodeRightClickMenuKind(tableStore.selectedCells.length) ===
          'cell'
        ) {
          // Collapse to the right-clicked cell so the value menu and the
          // highlight act on the same single cell (restores the pre-3164
          // per-cell flow).
          tableStore.deselectAllCells(cellId);
          tableStore.deselectAllRows();
          tableStore.setSelectedStartCell({
            cellId,
            columnId: cell.column.id as NodeColumns,
            rowId,
          });
          tableStore.selectCell({
            cellId,
            rowId,
            columnId: cell.column.id as NodeColumns,
          });
          if (
            document.activeElement &&
            'blur' in document.activeElement &&
            typeof document.activeElement.blur === 'function'
          ) {
            document.activeElement?.blur();
          }
          setMenuKind('cell');
          if (contextMenuState === 'closed') {
            setContextMenuState('opened');
          }
          return;
        }

        // Row intent. Mirror the index-cell selection: keep an existing
        // multi-row selection that already includes this row, otherwise
        // collapse to it.
        tableStore.deselectAllCells();
        if (
          tableStore.selectedRows.size >= 1 &&
          !tableStore.isSelectedRow(rowId)
        ) {
          tableStore.deselectAllRows();
        }
        tableStore.selectRow(rowId);
        setMenuKind('row');
        if (contextMenuState === 'closed') {
          setContextMenuState('opened');
        }
        // PSTUDIO-3337 / PSTUDIO-3547: proactively refresh the in-memory node
        // bundle from the SYSTEM clipboard when the menu opens so the row menu's
        // Paste / Paste above / Paste below items enable (canPasteRowAt) AND
        // reflect the LATEST copy across tabs / solutions ("last copy wins" — a
        // cross-tab / cross-solution copy lives only on the SYSTEM clipboard).
        // Fire-and-forget: MobX re-renders the (already-open) menu when the async
        // read lands; the paste itself re-refreshes before writing. Skipped for a
        // same-tab CUT (isClipboardCut) — hydrating would force isRowCut=false and
        // turn the move into a copy.
        if (!tableStore.isClipboardCut) {
          void tableStore.hydrateRowClipboardFromSystem();
        }
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // PSTUDIO-3990 (a11y): `aria-invalid` + `aria-describedby` so the error is
      // not conveyed by the red paint alone.
      {...resolveCellErrorAria(errorMessage, cellId)}
      // available classes: c-editor-table-td--warning c-editor-table-td--error
      className={`c-editor-table-td c-editor-table-td--${cell.column.id} ${pinning.classNames} ${isStarterCell ? 'is-selection-start' : ''} ${isSelected ? 'is-selected' : ''} ${isCellCopiedOrCut ? 'c-editor-table-td--copied' : ''} ${isCellCut ? 'c-editor-table-td--cut' : ''} ${errorMessage ? 'c-editor-table-td--error' : ''} ${coworkerFocusColorIndex != null ? `c-editor-table-td--coworker-focus c-editor-table-td--coworker-focus-${coworkerFocusColorIndex}` : ''} ${coworkerRangeColorIndex != null ? `c-editor-table-td--coworker-range c-editor-table-td--coworker-range-${coworkerRangeColorIndex}` : ''}`}
      style={{
        ...pinning.styles,
        ...(hidden ? { display: 'none' } : {}),
      }}
    >
      {coworkerFocusLabel != null && (
        <div className="c-editor-table-td__coworker-focus-label" aria-hidden>
          {coworkerFocusLabel}
        </div>
      )}
      {cellContent}

      <CellErrorTooltip
        cellId={cellId}
        errorMessage={errorMessage}
        active={isTooltipInitiallyActive}
      />

      {tableStore.isEditing() &&
        contextMenuState !== 'closed' &&
        (menuKind === 'cell' ? (
          <CellValueContextMenu
            rowId={rowId}
            columnId={cell.column.id as NodeColumns}
            cellId={cellId}
            rowIndex={index}
            active={contextMenuState === 'opened'}
            onRequestClose={() => {
              // 'closing' triggers the 100ms unmount timer (via useEffect) and
              // drops focus. The `data-popover-open` marker is NOT cleared here
              // — it is cleared from the STATE (see the effect above), so a menu
              // reopened between this close and the timer keeps its marker
              // instead of losing it to whichever close path happened to run
              // (the same fix the row menu already carries).
              setContextMenuState('closing');
              if (
                document.activeElement &&
                'blur' in document.activeElement &&
                typeof document.activeElement.blur === 'function'
              ) {
                document.activeElement?.blur();
              }
            }}
          />
        ) : (
          <RowContextMenu
            rowId={rowId}
            triggerId={cellId}
            rowIndex={index}
            active={contextMenuState === 'opened'}
            onRequestClose={() => setContextMenuState('closing')}
          />
        ))}
    </td>
  );
};

const ForwardedCell = forwardRef(CellComponent);
export const Cell = observer(ForwardedCell);
